import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/auth';

const BulletinPrint = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bulletin, setBulletin] = useState(null);
  const [student, setStudent] = useState(null);
  const [classInfo, setClassInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBulletin();
  }, [id]);

  const fetchBulletin = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const res = await api.get(`/report-cards/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBulletin(res.data);
      setStudent(res.data.student);
      setClassInfo(res.data.class);
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.response?.data?.error || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getAppreciation = (avg) => {
    if (avg >= 16) return 'Excellent';
    if (avg >= 14) return 'Très Bien';
    if (avg >= 12) return 'Assez Bien';
    if (avg >= 10) return 'Passable';
    return 'Insuffisant';
  };

  const getMentionRow = (avg, label) => {
    const active = (
      (label === 'Félicitations' && avg >= 16) ||
      (label === 'Encouragements' && avg >= 14 && avg < 16) ||
      (label === "Tableau d'honneur" && avg >= 12 && avg < 14) ||
      (label === 'Passable' && avg >= 10 && avg < 12) ||
      (label === 'Insuffisant' && avg < 10)
    );
    return active ? '☑' : '☐';
  };

  if (loading) return <div className="p-6 text-center">Chargement du bulletin...</div>;
  if (error) return <div className="p-6 text-center text-red-600">❌ {error}</div>;
  if (!bulletin) return <div className="p-6 text-center">Bulletin introuvable</div>;

  const subjects = bulletin.data?.subjects || [];
  const avg = bulletin.general_average || 0;

  let totalDevoirs = 0, totalCompos = 0, totalCoeff = 0, totalMoyCoef = 0;
  subjects.forEach((s) => {
    totalCoeff += s.coefficient || 0;
    totalMoyCoef += (s.average || 0) * (s.coefficient || 0);
    s.grades?.forEach((g) => {
      if (g.type === 'devoir') totalDevoirs += parseFloat(g.score) || 0;
      if (g.type === 'composition') totalCompos += parseFloat(g.score) || 0;
    });
  });

  // Calcule la moyenne générale (Total Moy x Coef / Total Coef)
  const moyenneGenerale = totalCoeff > 0 ? (totalMoyCoef / totalCoeff).toFixed(2) : '0.00';

  return (
    <div className="bg-gray-100 min-h-screen">
      {/* Bouton d'impression (caché à l'impression) */}
      <div className="print:hidden bg-white shadow-md p-4 flex justify-between items-center sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-[#E67E22] font-medium">← Retour</button>
        <button onClick={handlePrint} className="bg-[#E67E22] text-white px-6 py-2 rounded-lg hover:bg-[#D35400] font-medium">🖨️ Imprimer / PDF</button>
      </div>

      {/* Format A4 */}
      <div className="print:shadow-none print:m-0 max-w-[210mm] mx-auto my-6 bg-white shadow-lg p-8 print:p-4 text-[11px] leading-tight font-sans">
        
        {/* 1. EN-TÊTE */}
        <div className="flex justify-between items-start border-b-2 border-black pb-2 mb-2">
          <div className="w-20 h-14 flex items-center justify-center border border-black p-1">
            {/* ⬇️ DRAPEAU DU SÉNÉGAL ⬇️ */}
            <img src="/images/flag-senegal.png" alt="Drapeau Sénégal" className="w-full h-full object-contain" />
          </div>
          <div className="text-center flex-1 px-4">
            <p className="font-bold text-[10px]">République du Sénégal</p>
            <p className="text-[9px]">Ministère de l'Éducation nationale</p>
            <p className="font-bold text-[12px] uppercase">LYCÉE {bulletin.establishment?.name || 'BAMBEY'}</p>
            <p className="text-[10px] font-semibold">ANNÉE SCOLAIRE : {bulletin.academic_year}</p>
          </div>
          <div className="w-20 h-14 flex items-center justify-center border border-black p-1">
             {/* ⬇️ LOGO DE L'ÉCOLE ⬇️ */}
             <img src="/images/logo12.png" alt="Logo" className="h-10 object-contain" />
          </div>
        </div>

        {/* 2. INFOS ÉLÈVE (Grille 3 colonnes) */}
        <div className="grid grid-cols-3 border border-black mb-4 text-[10px]">
          {/* Colonne 1 */}
          <div className="border-r border-black p-2 space-y-1">
            <p>Tél : <span className="font-semibold">{bulletin.establishment?.phone || '-'}</span></p>
            <p>Email : <span className="font-semibold">{bulletin.establishment?.email || '-'}</span></p>
            <p>Niveau : <span className="font-semibold uppercase">{classInfo?.level || '-'}</span></p>
            <p>Série : <span className="font-semibold">{student?.serie || '-'}</span></p>
            <p>Effectif : <span className="font-semibold">{bulletin.total_students || '-'}</span></p>
            <p>Moyenne classe : <span className="font-semibold">{bulletin.class_average?.toFixed(2) || '-'}</span></p>
          </div>
          {/* Colonne 2 */}
          <div className="border-r border-black p-2 space-y-1">
            <p>IEN : <span className="font-semibold">{student?.ine || '-'}</span></p>
            <p>Classe : <span className="font-semibold">{classInfo?.name || '-'}</span></p>
            <p>Classe doublée : <span className="font-semibold">NEANT</span></p>
            <p>Prénom(s) : <span className="font-semibold">{student?.user?.name?.split(' ')[0] || '-'}</span></p>
            <p>Nom : <span className="font-semibold">{student?.user?.name?.split(' ').slice(1).join(' ') || '-'}</span></p>
            <p>Né(e) le : <span className="font-semibold">{student?.birth_date || '-'}</span> à <span className="font-semibold">{student?.birth_place || '-'}</span></p>
          </div>
          {/* Colonne 3 */}
          <div className="p-2 space-y-1">
            <p>SEXE : <span className="font-semibold">{student?.sexe === 'M' ? 'MASCULIN' : student?.sexe === 'F' ? 'FÉMININ' : '-'}</span></p>
            <p>Matricule : <span className="font-semibold">{student?.matricule || '-'}</span></p>
            <p>Statut : <span className="font-semibold">Actif</span></p>
          </div>
        </div>

        {/* 3. TITRE */}
        <div className="bg-gray-300 text-center py-1 font-bold text-[12px] border border-black mb-2 uppercase">
          BULLETIN DU {bulletin.period_label || 'PREMIER SEMESTRE'}
        </div>

        {/* 4. TABLEAU DE NOTES */}
        <table className="w-full border-collapse border border-black text-[10px] mb-4">
          <thead>
            <tr className="bg-gray-200 font-bold text-center">
              <th className="border border-black px-2 py-1 text-left w-1/4">Disciplines</th>
              <th className="border border-black px-1 py-1">Devoirs</th>
              <th className="border border-black px-1 py-1">Compos</th>
              <th className="border border-black px-1 py-1">Moy</th>
              <th className="border border-black px-1 py-1">Coef</th>
              <th className="border border-black px-1 py-1">Moy×Coef</th>
              <th className="border border-black px-1 py-1">Rang</th>
              <th className="border border-black px-1 py-1">Appréciations</th>
            </tr>
          </thead>
          <tbody>
            {subjects.map((s, i) => {
              // On calcule la moyenne pondérée des devoirs
              const devoirs = s.grades?.filter((g) => g.type === 'devoir') || [];
              let totalD = 0, coefD = 0;
              devoirs.forEach((d) => { totalD += (parseFloat(d.score)||0) * (parseFloat(d.coefficient)||1); coefD += parseFloat(d.coefficient)||1; });
              const moyD = coefD > 0 ? (totalD / coefD).toFixed(2) : '-';

              const compos = s.grades?.filter((g) => g.type === 'composition') || [];
              const moyC = compos.length > 0 ? compos[0].score.toFixed(2) : '-';

              return (
                <tr key={i} className="text-center">
                  <td className="border border-black px-2 py-1 text-left font-medium">{s.subject_name}</td>
                  <td className="border border-black px-1 py-1">{moyD}</td>
                  <td className="border border-black px-1 py-1">{moyC}</td>
                  <td className="border border-black px-1 py-1 font-bold">{s.average?.toFixed(2)}</td>
                  <td className="border border-black px-1 py-1">{s.coefficient}</td>
                  <td className="border border-black px-1 py-1">{((s.average || 0) * (s.coefficient || 0)).toFixed(2)}</td>
                  <td className="border border-black px-1 py-1">{s.rank || '-'}</td>
                  <td className="border border-black px-2 py-1 text-left">{getAppreciation(s.average)}</td>
                </tr>
              );
            })}
            {/* Ligne Totaux */}
            <tr className="bg-gray-200 font-bold text-center">
              <td className="border border-black px-2 py-1 text-left">Totaux</td>
              <td className="border border-black px-1 py-1">{totalDevoirs.toFixed(2)}</td>
              <td className="border border-black px-1 py-1">{totalCompos.toFixed(2)}</td>
              <td className="border border-black px-1 py-1"></td>
              <td className="border border-black px-1 py-1">{totalCoeff}</td>
              <td className="border border-black px-1 py-1">{totalMoyCoef.toFixed(2)}</td>
              <td className="border border-black px-1 py-1"></td>
              <td className="border border-black px-1 py-1"></td>
            </tr>
          </tbody>
        </table>

        {/* 5. MOYENNE, RANG, ABSENCES */}
        <div className="border border-black p-2 mb-4 text-[11px] font-bold flex justify-between">
          <span>Moyenne : {moyenneGenerale}</span>
          <span>Rang : {bulletin.rank || '-'}</span>
          <span>Retards : {bulletin.lates_count || 0} heure(s) {bulletin.lates_minutes || 0} minute(s)</span>
          <span>Absences : {bulletin.absences_count || 0} dont {bulletin.absences_justified || 0} justifiée(s)</span>
        </div>

        {/* 6. BLOC BAS DE PAGE (Mentions + Observations) */}
        <div className="grid grid-cols-2 gap-4">
          {/* Gauche : Grille des mentions */}
          <div>
            <table className="w-full border-collapse border border-black text-[10px]">
              <tbody>
                <tr>
                  <td className="border border-black px-2 py-1">Travail excellent</td>
                  <td className="border border-black px-2 py-1 text-center">{getMentionRow(avg, 'Félicitations')}</td>
                  <td className="border border-black px-2 py-1">Félicitations</td>
                  <td className="border border-black px-2 py-1 text-center">{getMentionRow(avg, 'Félicitations')}</td>
                </tr>
                <tr>
                  <td className="border border-black px-2 py-1">Satisfaisant doit continuer</td>
                  <td className="border border-black px-2 py-1 text-center">{getMentionRow(avg, 'Encouragements')}</td>
                  <td className="border border-black px-2 py-1">Encouragements</td>
                  <td className="border border-black px-2 py-1 text-center">{getMentionRow(avg, 'Encouragements')}</td>
                </tr>
                <tr>
                  <td className="border border-black px-2 py-1">Peut mieux faire</td>
                  <td className="border border-black px-2 py-1 text-center">{getMentionRow(avg, "Tableau d'honneur")}</td>
                  <td className="border border-black px-2 py-1">Tableau d'honneur</td>
                  <td className="border border-black px-2 py-1 text-center">{getMentionRow(avg, "Tableau d'honneur")}</td>
                </tr>
                <tr>
                  <td className="border border-black px-2 py-1">Insuffisant</td>
                  <td className="border border-black px-2 py-1 text-center">{getMentionRow(avg, 'Insuffisant')}</td>
                  <td className="border border-black px-2 py-1">Passable</td>
                  <td className="border border-black px-2 py-1 text-center">{getMentionRow(avg, 'Passable')}</td>
                </tr>
                <tr>
                  <td className="border border-black px-2 py-1">Risque de redoubler</td>
                  <td className="border border-black px-2 py-1 text-center"></td>
                  <td className="border border-black px-2 py-1">Doit redoubler d'effort</td>
                  <td className="border border-black px-2 py-1 text-center"></td>
                </tr>
                <tr>
                  <td className="border border-black px-2 py-1">Risque l'exclusion</td>
                  <td className="border border-black px-2 py-1 text-center"></td>
                  <td className="border border-black px-2 py-1">Avertissement</td>
                  <td className="border border-black px-2 py-1 text-center"></td>
                </tr>
                <tr>
                  <td className="border border-black px-2 py-1"></td>
                  <td className="border border-black px-2 py-1"></td>
                  <td className="border border-black px-2 py-1">Blâme</td>
                  <td className="border border-black px-2 py-1"></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Droite : Observations et Signature */}
          <div className="flex flex-col justify-between">
            <div className="border border-black p-2 h-full">
              <p className="font-bold text-[10px] mb-2">Observations du conseil des professeurs</p>
              <p className="text-[10px] italic">{bulletin.observations || bulletin.appreciation || ''}</p>
            </div>
            <div className="text-right mt-4">
              <p className="text-[11px] font-bold">Le Chef d'établissement</p>
              <div className="h-12"></div>
              <p className="text-[9px]">(Signature & Cachet)</p>
            </div>
          </div>
        </div>

        {/* 7. PIED DE PAGE (QR CODES) */}
        <div className="flex justify-between items-end mt-8 border-t border-black pt-2">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-200 border border-black mx-auto flex items-center justify-center text-[8px] text-gray-500">QR CODE</div>
            <p className="text-[9px] mt-1">Informations Élève</p>
          </div>
          <div className="text-center text-[8px] max-w-sm">
            <p>N.B. : Ce bulletin n'est délivré qu'une seule fois. Toute demande de duplicata pourrait faire l'objet d'une contrepartie financière.</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-200 border border-black mx-auto flex items-center justify-center text-[8px] text-gray-500">QR CODE</div>
            <p className="text-[9px] mt-1">Ouvrir le bulletin</p>
          </div>
        </div>
        
        <div className="text-right mt-2">
          <p className="text-[8px] text-gray-600">Ce bulletin est édité le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR')}</p>
        </div>
      </div>
    </div>
  );
};

export default BulletinPrint;